import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { NoData, Spinner } from '@rio-cloud/rio-uikit';
import { fetchVehicleMaintenanceHistory, type VehicleMaintenanceHistory } from '../../../../../api/tas';

const columns = [
    'intl-msg:support.vehicleHistory.columns.dealerDnCode',
    'intl-msg:support.vehicleHistory.columns.dealer',
    'intl-msg:support.vehicleHistory.columns.revisionCode',
    'intl-msg:support.vehicleHistory.columns.revisionDate',
    'intl-msg:support.vehicleHistory.columns.serviceOrder',
    'intl-msg:support.vehicleHistory.columns.revisionKm',
    'intl-msg:support.vehicleHistory.columns.maintenanceGroup',
];

interface Props {
    chassis?: string;
}

export default function Revisions({ chassis }: Props) {
    const intl = useIntl();
    const [rows, setRows] = useState<VehicleMaintenanceHistory[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        if (!chassis) {
            setRows([]);
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        setHasError(false);

        fetchVehicleMaintenanceHistory(chassis)
            .then(data => {
                if (!cancelled) {
                    setRows(data);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setRows([]);
                    setHasError(true);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [chassis]);

    if (isLoading) {
        return (
            <div className='display-flex justify-content-center padding-20'>
                <Spinner isDoubleSized />
            </div>
        );
    }

    if (hasError || !rows.length) {
        return (
            <NoData
                text={intl.formatMessage({
                    id: hasError
                        ? 'intl-msg:support.vehicleHistory.loadError'
                        : 'intl-msg:support.vehicleHistory.noData',
                })}
                className='text-size-16 padding-20'
            />
        );
    }

    return (
        <div className='table-responsive'>
            <table className='table table-head-filled'>
                <thead>
                    <tr>
                        {columns.map(column => (
                            <th key={column}>{intl.formatMessage({ id: column })}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map(row => (
                        <tr key={row.id}>
                            <td>{row.dnCode}</td>
                            <td>{row.dealer}</td>
                            <td>{row.revisionCode}</td>
                            <td>{row.revisionDate ? moment.utc(row.revisionDate).format('DD/MM/YYYY') : '-'}</td>
                            <td>{row.serviceOrder != null ? intl.formatNumber(row.serviceOrder) : ''}</td>
                            <td>{row.mileage != null ? intl.formatNumber(row.mileage) : ''}</td>
                            <td>{row.maintenanceGroup}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
